import React from 'react';
import { CaretDown, Info } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';

import {
  Button as DskButton,
  Checkbox as DskCheckbox,
  Select as DskSelect,
  Textarea as DskTextarea,
} from '../../components/desktop';
import { Modal } from '../../components/desktop/complex';
import {
  Checkbox as DsCheckbox,
  Cta,
  Sheet,
  SheetBody,
  SheetFooter,
  SheetHeader,
  Textarea as DsTextarea,
} from '../../components/mobile';

// The labelled control, the field stack scaffolding, the inline error line and
// the form level banner are already solved in `patterns/auth`, and they are
// solved track-neutrally, which is exactly what a modal form needs too. So they
// are imported and re-exported here under form names rather than rebuilt.
// If a third pattern ever needs them, they get promoted to `patterns/form/` and
// both callers change; two callers is not yet that moment.
import { AuthBanner, AuthError, AuthField, AuthInput } from '../auth';

import './modal-form.css';

/**
 * The modal form pattern. NINE surfaces share it.
 *
 *   BuyerEditProfileForm   EditProfileForm      EditDeliveryForm
 *   EditStoreInfoForm      BankDetailsForm      AddressFormModal
 *   PayoutRequestModal     AddressModal         RejectionForm
 *
 * Every one of those was a hand-rolled `<form>` with its own `.form-group`,
 * its own `.error-text`, its own right-aligned `.modal-actions` and, in four
 * cases, its own stylesheet. Four of those stylesheets drew the same 8px gap
 * under the same 14px label. That is the promotion test in `patterns/README.md`
 * met three times over.
 *
 * THE 1024 HANDOFF, WHICH IS THE WHOLE REASON THIS IS A PATTERN
 * ------------------------------------------------------------
 * `README.md` section 5: a form modal on desktop is a BOTTOM SHEET on mobile.
 * That is a real DOM difference, not a padding one. A `Modal` portals to
 * `<body>`, carries `.dsk-card__header` chrome and a right-aligned action row;
 * a `Sheet` is absolutely positioned inside the app shell, has a grabber, a
 * scrolling body and a footer whose CTAs never scroll away from the thumb. So
 * `useViewport()` picks ONE and only that one mounts. Rendering both and hiding
 * one would double the DOM, put a second copy of every field in the tab order
 * and ship two submit handlers for one form.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * No fetch, no navigate, no toast, no validation. A caller owns its own state
 * and hands this `values`, `errors` and `onSubmit`. That is what lets every one
 * of the nine keep its existing behaviour byte for byte while losing its CSS.
 */

/* ------------------------------------------------------------------ fields */

/**
 * The labelled control. Desktop `.dsk-field`, mobile `.ds-field`, one API.
 *
 * `full` spans every column of the surrounding `ModalFields` grid. It is a no-op
 * below the handoff, where the grid is a single-column stack and the modifier
 * would be a `dsk-` class in a `ds-` tree.
 */
export function ModalField({ full = false, optional = false, label, className, ...rest }) {
  const { isDesktop } = useViewport();

  // REQUIRED IS THE NORM on Sleevr forms, so the OPTIONAL case is the one that
  // gets marked. Marking the required fields on a form where five of six are
  // required tells the user nothing; marking the one that is not does.
  //
  // Desktop has `.dsk-label__opt` for this and `Field` renders it from an
  // `optional` prop. The mobile label has no such slot, and forwarding the prop
  // would land `optional={true}` on a plain div and trip React's non-boolean
  // attribute warning, so on that track the marker is folded into the label.
  const desktopOptional = isDesktop && optional ? { optional: true } : {};
  const mobileLabel =
    !isDesktop && optional && label ? (
      <>
        {label} <span className="dsa-mf__optional">Optional</span>
      </>
    ) : (
      label
    );

  return (
    <AuthField
      label={mobileLabel}
      className={cn(full && isDesktop && 'dsk-field--full', className)}
      {...desktopOptional}
      {...rest}
    />
  );
}

/**
 * The track's text input.
 *
 * `locked` is the read-only value treatment, for a field that is shown because
 * the user needs to see WHICH account they are editing but that this form
 * cannot write: a buyer's email address is the only one today. It is `readOnly`
 * and never `disabled`, because a disabled control is skipped by the tab order
 * and a screen reader user would never reach the value at all.
 *
 * The mobile `Input` has the treatment built in. The desktop one does not, and
 * forwarding an unknown `locked` prop to it would land the attribute on a real
 * `<input>` and trip React's non-boolean attribute warning, so the desktop
 * branch translates it into `readOnly` plus a class.
 */
export const ModalInput = React.forwardRef(function ModalInput(
  { locked = false, className, ...rest },
  ref,
) {
  const { isDesktop } = useViewport();

  if (isDesktop && locked) {
    return (
      <AuthInput
        ref={ref}
        readOnly
        className={cn('dsa-mf__locked', className)}
        {...rest}
      />
    );
  }

  return <AuthInput ref={ref} locked={locked || undefined} className={className} {...rest} />;
});

/** The inline error line: glyph plus literal text, so colour is never alone. */
export const ModalError = AuthError;

/** A form-level message that belongs to the submission, not to one field. */
export const ModalBanner = AuthBanner;

/**
 * The field grid.
 *
 * Desktop uses the library's own `.dsk-fields`, which is
 * `repeat(N, minmax(0, 1fr))` upstream and therefore cannot be overflowed by a
 * long value (README section 10). Below the handoff a modal form is a sheet on
 * a phone and every field is full width, so the mobile class is a plain stack
 * and `cols` is ignored rather than faked.
 */
export function ModalFields({ cols = 2, className, ...rest }) {
  const { isDesktop } = useViewport();
  if (isDesktop) {
    const colsClass = { 1: 'dsk-fields--1', 2: null, 3: 'dsk-fields--3' }[cols];
    return <div className={cn('dsk-fields', colsClass, className)} {...rest} />;
  }
  return <div className={cn('dsa-mf__stack', className)} {...rest} />;
}

/** Spans every column of a `ModalFields` grid. Ignored on the mobile stack. */
export function ModalFieldFull({ className, ...rest }) {
  const { isDesktop } = useViewport();
  return <div className={cn(isDesktop && 'dsk-field--full', className)} {...rest} />;
}

/**
 * The track's multi-line input.
 *
 * The desktop textarea takes `error`; the mobile one does not, and would put a
 * non-boolean `error` attribute on the DOM, so on that track it becomes the
 * error modifier plus `aria-invalid`. `filled` is the mobile track's own rule
 * (borderless once it holds a value) and is derived rather than asked for.
 */
export const ModalTextarea = React.forwardRef(function ModalTextarea(
  { error = false, className, ...rest },
  ref,
) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return <DskTextarea ref={ref} error={error} className={className} {...rest} />;
  }

  return (
    <DsTextarea
      ref={ref}
      filled={Boolean(rest.value)}
      aria-invalid={error ? true : undefined}
      className={cn(error && 'ds-input--error', className)}
      {...rest}
    />
  );
});

/**
 * The one select.
 *
 * The app shipped three (`Select`, `CustomSelect`, `DropDown`). Desktop gets
 * the library's custom shell, which keeps a real `<select>` in the DOM for form
 * semantics; below the handoff the native control is the right answer, because
 * a phone's own picker beats anything we can draw.
 */
export const ModalSelect = React.forwardRef(function ModalSelect(
  { options = [], value, onChange, placeholder = 'Select', error = false, className, ...rest },
  ref,
) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return (
      <DskSelect
        ref={ref}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        className={className}
        {...rest}
      />
    );
  }

  const items = options.map((opt) =>
    typeof opt === 'object' && opt !== null
      ? { value: String(opt.value), label: opt.label ?? String(opt.value) }
      : { value: String(opt), label: String(opt) },
  );

  return (
    <span className="dsa-mf__native">
      <select
        ref={ref}
        className={cn('ds-input', error && 'ds-input--error', className)}
        value={value}
        aria-invalid={error ? true : undefined}
        onChange={(event) => onChange && onChange(event.target.value)}
        {...rest}
      >
        {value === undefined || value === '' ? <option value="">{placeholder}</option> : null}
        {items.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      <Icon as={CaretDown} size="sm" className="dsa-mf__native-caret" />
    </span>
  );
});

/**
 * A checkbox and its sentence on one row.
 *
 * The control is a BARE `<input>`, which is a void element, so the label is a
 * sibling and never a child. Passing children to `Checkbox` throws "input is a
 * void element tag" and takes the tree down, which is why the label lives here.
 */
export function ModalCheck({ id, error, children, className, ...rest }) {
  const { isDesktop } = useViewport();
  const Check = isDesktop ? DskCheckbox : DsCheckbox;
  const uid = React.useId();
  const controlId = id || `${uid}-check`;

  return (
    <div className={cn('dsa-mf__check', className)}>
      <div className="dsa-mf__check-row">
        <Check id={controlId} aria-invalid={error ? true : undefined} {...rest} />
        <label className="dsa-mf__check-text" htmlFor={controlId}>
          {children}
        </label>
      </div>
      <ModalError>{error}</ModalError>
    </div>
  );
}

/**
 * A file picker.
 *
 * Kept as a real `<input type="file">` rather than a styled button plus a
 * hidden input, because the native control is the only one that reports the
 * chosen filename to assistive tech without us reimplementing it. `note` is
 * where the size limit goes, and it is stated BEFORE the user picks, not after
 * the upload is rejected.
 */
export const ModalFile = React.forwardRef(function ModalFile(
  // `error` arrives from the surrounding ModalField, which clones it onto every
  // child control. The track inputs consume it; a raw <input> would put a
  // non-boolean `error` attribute on the DOM, so it is caught here and turned
  // into the aria state it actually means.
  { note, error = false, className, ...rest },
  ref,
) {
  return (
    <span className={cn('dsa-mf__file', className)}>
      <input ref={ref} type="file" aria-invalid={error ? true : undefined} {...rest} />
      {note ? <span className="dsa-mf__file-note">{note}</span> : null}
    </span>
  );
});

/**
 * A read-only value inside a form: the wallet balance a payout is drawn from,
 * the remaining balance after it. Not an input, so it is not a locked field
 * either; it is a figure the form is reasoning about.
 */
export function ModalReadout({ label, value, tone = 'default', className }) {
  return (
    <div className={cn('dsa-mf__readout', className)}>
      <span className="dsa-mf__readout-label">{label}</span>
      <span
        className={cn('dsa-mf__readout-value', tone === 'error' && 'is-error')}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * The quiet advisory line inside a modal form.
 *
 * This is what replaces a permanently disabled checkbox or a permanently
 * disabled field: a control that cannot be operated is not a control, and
 * saying the thing plainly is both shorter and honest.
 */
export function ModalNote({ children, className }) {
  return (
    <p className={cn('dsa-mf__note', className)}>
      <Icon as={Info} size="sm" />
      <span>{children}</span>
    </p>
  );
}

/** A titled group inside a longer modal form, e.g. "Personal information". */
export function ModalGroup({ title, children, className }) {
  const { isDesktop } = useViewport();
  return (
    <div className={cn('dsa-mf__group', className)}>
      {title ? (
        <h3 className={isDesktop ? 'dsa-mf__group-title' : 'ds-section-title ds-section-title--sm'}>
          {title}
        </h3>
      ) : null}
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------- body */

function Actions({
  submitLabel,
  submittingLabel,
  submitting,
  submitDisabled,
  cancelLabel,
  onCancel,
  showCancel,
  secondary,
}) {
  const { isDesktop } = useViewport();
  const label = submitting ? submittingLabel || submitLabel : submitLabel;

  if (isDesktop) {
    return (
      <div className="dsa-mf__actions">
        {showCancel ? (
          <DskButton variant="ghost" onClick={onCancel} disabled={submitting}>
            {cancelLabel}
          </DskButton>
        ) : null}
        {secondary ? (
          <DskButton variant="ghost" onClick={secondary.onClick} disabled={submitting}>
            {secondary.label}
          </DskButton>
        ) : null}
        <DskButton type="submit" variant="primary" disabled={submitting || submitDisabled}>
          {label}
        </DskButton>
      </div>
    );
  }

  return (
    <div className="dsa-mf__actions">
      {showCancel ? (
        <Cta variant="ghost" block onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </Cta>
      ) : null}
      {secondary ? (
        <Cta variant="ghost" block onClick={secondary.onClick} disabled={submitting}>
          {secondary.label}
        </Cta>
      ) : null}
      <Cta type="submit" variant="solid" block disabled={submitting || submitDisabled}>
        {label}
      </Cta>
    </div>
  );
}

/**
 * The form body on its own, WITHOUT the modal or sheet around it.
 *
 * This exists for one real case: `RejectionForm` is mounted by three admin
 * screens that each supply their own `<Modal>`, and those screens belong to
 * another batch. Nesting a second dialog inside theirs would trap focus twice
 * and lock body scroll twice. So the body is separable, and `ModalForm` is the
 * body plus the layer.
 */
export function ModalFormBody({
  onSubmit,
  onCancel,
  heading,
  error,
  message,
  children,
  submitLabel = 'Save',
  submittingLabel,
  submitting = false,
  submitDisabled = false,
  cancelLabel = 'Cancel',
  showCancel = true,
  secondary,
  className,
  ...rest
}) {
  return (
    <form
      className={cn('dsa-mf', className)}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (onSubmit) onSubmit(event);
      }}
      {...rest}
    >
      {heading ? <h3 className="dsa-mf__heading">{heading}</h3> : null}
      <ModalBanner tone="success">{message}</ModalBanner>
      <ModalBanner tone="error">{error}</ModalBanner>

      <div className="dsa-mf__body">{children}</div>

      <Actions
        submitLabel={submitLabel}
        submittingLabel={submittingLabel}
        submitting={submitting}
        submitDisabled={submitDisabled}
        cancelLabel={cancelLabel}
        onCancel={onCancel}
        showCancel={showCancel && Boolean(onCancel)}
        secondary={secondary}
      />
    </form>
  );
}

/* ------------------------------------------------------------------- layer */

/**
 * The whole thing: a form in the right layer for the viewport.
 *
 * `titleIcon` is a glyph that belongs to the TITLE and is rendered on its left,
 * on the same line, on BOTH tracks. It is not a hero illustration and there is
 * no slot for one -- a glyph parked in the body above the copy pushes the words
 * down for no gain and reads as an orphan on a 390px sheet.
 *
 * It goes INSIDE the heading element on both tracks rather than beside it, and
 * that is the whole reason this needs no change to `Modal`, `Sheet` or the
 * vendored CSS. Both layers own their header row: `.dsk-card__header` also
 * holds the close button, `.ds-sheet__header` also holds `trailing`, and both
 * are `justify-content: space-between`, so a glyph added as a SIBLING of the
 * title would be pushed to the far edge. Both layers also label the dialog
 * from the heading's id, and a decorative `Icon` is already `aria-hidden`, so
 * a child contributes nothing to the accessible name. The two `dsa-mf-modal` /
 * `dsa-mf-sheet` rules in `modal-form.css` make the heading the flex row.
 *
 * @param {{
 *   open: boolean,
 *   title: string,
 *   titleIcon?: React.ReactNode,
 *   subtitle?: string,
 *   onClose: () => void,
 *   onSubmit: (event: React.FormEvent) => void,
 *   submitLabel?: string,
 *   submittingLabel?: string,
 *   submitting?: boolean,
 *   submitDisabled?: boolean,
 *   cancelLabel?: string,
 *   showCancel?: boolean,
 *   secondary?: { label: string, onClick: () => void },
 *   error?: string,
 *   message?: string,
 *   children: React.ReactNode
 * }} props
 */
export function ModalForm({
  open,
  title,
  titleIcon,
  subtitle,
  onClose,
  className,
  ...body
}) {
  const { isDesktop } = useViewport();

  const heading = titleIcon ? (
    <>
      <span className="dsa-mf__title-icon">{titleIcon}</span>
      {title}
    </>
  ) : (
    title
  );

  /* Neither branch is gated by an early return on `open`. `Modal` renders null
     while closed on its own, and `Sheet` MUST stay mounted through its 200ms
     slide-down -- pulling the node out on the closing tick is what left the
     form sheet with an entry animation and no exit. */
  if (isDesktop) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={heading}
        className={cn('dsa-mf-modal', className)}
      >
        {subtitle ? <p className="dsa-mf__subtitle">{subtitle}</p> : null}
        <ModalFormBody onCancel={onClose} {...body} />
      </Modal>
    );
  }

  return (
    <Sheet open={Boolean(open)} onClose={onClose} className={cn('dsa-mf-sheet', className)}>
      <SheetHeader title={heading} subtitle={subtitle} />
      <SheetBody>
        <ModalFormBody onCancel={onClose} {...body} />
      </SheetBody>
    </Sheet>
  );
}

/**
 * A destructive confirmation.
 *
 * There is no `.dsk-btn--danger` in the vendored CSS and none is invented here.
 * A destructive command is the ink primary whose LABEL carries the consequence
 * ("Delete address", not "OK"), reached from a ghost or icon trigger, with this
 * dialog doing the real work of making the user say it twice.
 */
export function ConfirmForm({
  open,
  title,
  message,
  detail,
  confirmLabel = 'Delete',
  confirmingLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onClose,
  loading = false,
}) {
  return (
    <ModalForm
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={onConfirm}
      submitLabel={confirmLabel}
      submittingLabel={confirmingLabel}
      submitting={loading}
      cancelLabel={cancelLabel}
    >
      <p className="dsa-mf__confirm">{message}</p>
      {detail ? <p className="dsa-mf__confirm-detail">{detail}</p> : null}
    </ModalForm>
  );
}

/** Re-exported so a page can pin a sheet footer without importing two barrels. */
export { SheetFooter };

export default ModalForm;
